import type { FieldValues, Path } from 'react-hook-form';

import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import type { SliderMark } from '@repo/sds/SDSSlider';
import { SDSSlider } from '@repo/sds/SDSSlider';
import { SDSText } from '@repo/sds/SDSText';

import type { ControlledFieldProps } from '../FormField/FormField';

/** Base configuration props specific to the Slider Input component. */
export interface BaseFormSliderInputProps<TFieldValues extends FieldValues> {
  /** Name identifier for the field within the form state. */
  name: Path<TFieldValues>;
  /** Text label displayed for the input. */
  label: string;
  /** Minimum value for the slider. */
  min: number;
  /** Maximum value for the slider. */
  max: number;
  /** Step increment for the slider. Defaults to 1. */
  step?: number;
  /** Optional marks to display on the slider. */
  marks?: SliderMark[];
  /** Helper text displayed below the slider. */
  helperText?: string;
  /** Format function for displaying the current value. */
  formatValue?: (value: number) => string;
  /** Indicates if the field is mandatory. */
  isRequired?: boolean;
  /** Data attribute for testing purposes. */
  dataTestId?: string;
}

/** Combined props for the FormSliderInput component, including RHF control props. */
interface FormSliderInputProps<TFieldValues extends FieldValues, TName extends Path<TFieldValues> = Path<TFieldValues>>
  extends BaseFormSliderInputProps<TFieldValues>,
    ControlledFieldProps<TFieldValues, TName> {}

/**
 * Renders an SDSSlider component integrated with React Hook Form,
 * receiving RHF state and handlers via the 'field' prop from a Controller wrapper.
 */
export function FormSliderInput<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues> = Path<TFieldValues>,
>({
  field,
  errorMessage,
  isDisabled,
  label,
  min,
  max,
  step = 1,
  marks,
  helperText,
  formatValue,
  isRequired = false,
  dataTestId,
}: FormSliderInputProps<TFieldValues, TName>) {
  const currentValue = typeof field.value === 'number' ? field.value : 0;
  const displayValue = formatValue ? formatValue(currentValue) : `${currentValue}`;

  const handleChange = (value: number) => {
    field.onChange(value);
  };

  return (
    <SDSFlexbox direction="column" gap="no-design-8" fullWidth>
      <SDSFlexbox justifyContent="space-between" alignItems="center">
        <SDSFlexbox gap="no-design-4" alignItems="center">
          <SDSText text={label} type="body-medium" />
          {isRequired && <SDSText text="*" type="body-medium" color="alert" />}
        </SDSFlexbox>
        <SDSText text={displayValue} type="body-medium" />
      </SDSFlexbox>
      <SDSSlider
        value={currentValue}
        onChange={handleChange}
        min={min}
        max={max}
        step={step}
        marks={marks}
        disabled={isDisabled}
        className={dataTestId}
      />
      {helperText && <SDSText text={helperText} type="body-small" color="muted" />}
      {errorMessage && <SDSText text={errorMessage} type="body-small" color="alert" />}
    </SDSFlexbox>
  );
}
